import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'
import { fade } from '@material-ui/core/styles/colorManipulator'
import DomainIcon from '@material-ui/icons/Domain'
import SearchIcon from '@material-ui/icons/Search'

import { useUserTags } from '../../contexts/UserTagsContext'
import { currencyFormatter } from '../../helpers'
import { UserTag } from '../../models/Users/types'

const BRAND_TEAL = '#008C85'

const DIALOG_MAX_HEIGHT = '92vh'

const BREAKDOWN_TABLE_VIEWPORT =
    'max(240px, min(calc(92vh - 300px), calc(100vh - 320px)))'

export interface PropertyInvoiceSummaryRow {
    apartment_id: number
    name: string
    invoice_count: number
    total_cost: number
}

function tagsForApartment(apartmentId: number, userTags: UserTag[]): UserTag[] {
    return userTags.filter((tag) =>
        (tag.apartments ?? []).includes(apartmentId),
    )
}

type SortKey = 'name' | 'invoice_count' | 'total_cost'

type SortState = {
    key: SortKey
    dir: 'asc' | 'desc'
}

export interface PropertyInvoiceBreakdownModalProps {
    open: boolean
    loading: boolean
    rows: PropertyInvoiceSummaryRow[]
    onClose: () => void
}

const defaultSort: SortState = { key: 'name', dir: 'asc' }

export const PropertyInvoiceBreakdownModal = ({
    open,
    loading,
    rows,
    onClose,
}: PropertyInvoiceBreakdownModalProps) => {
    const theme = useTheme()
    const { userTags } = useUserTags()
    const [search, setSearch] = useState('')
    const [sort, setSort] = useState<SortState>(defaultSort)
    const [selectedTagId, setSelectedTagId] = useState<number | null>(null)

    useEffect(() => {
        if (!open) {
            setSearch('')
            setSort(defaultSort)
            setSelectedTagId(null)
        }
    }, [open])

    const workingRows = useMemo(() => {
        if (selectedTagId == null) return rows
        const tag = userTags.find((t) => t.id === selectedTagId)
        const ids = tag?.apartments ?? []
        if (ids.length === 0) return []
        const allowed = new Set(ids)
        return rows.filter((r) => allowed.has(r.apartment_id))
    }, [rows, selectedTagId, userTags])

    const totals = useMemo(() => {
        return workingRows.reduce(
            (acc, r) => ({
                invoiceCount: acc.invoiceCount + r.invoice_count,
                totalCost: acc.totalCost + r.total_cost,
            }),
            { invoiceCount: 0, totalCost: 0 },
        )
    }, [workingRows])

    const displayedRows = useMemo(() => {
        const q = search.trim().toLowerCase()
        const filtered = q
            ? workingRows.filter((r) =>
                  (r.name || '').toLowerCase().includes(q),
              )
            : workingRows
        const { key, dir } = sort
        const mult = dir === 'asc' ? 1 : -1
        return [...filtered].sort((a, b) => {
            if (key === 'name') {
                return (
                    mult *
                    (a.name || '').localeCompare(b.name || '', undefined, {
                        sensitivity: 'base',
                    })
                )
            }
            if (key === 'invoice_count') {
                return mult * (a.invoice_count - b.invoice_count)
            }
            return mult * (a.total_cost - b.total_cost)
        })
    }, [workingRows, search, sort])

    const handleTagChange = useCallback(
        (event: React.ChangeEvent<{ value: unknown }>) => {
            const value = event.target.value
            setSelectedTagId(value === 'all' ? null : (value as number))
        },
        [],
    )

    const handleSort = useCallback((column: SortKey) => {
        setSort((prev) =>
            prev.key === column
                ? {
                      key: column,
                      dir: prev.dir === 'asc' ? 'desc' : 'asc',
                  }
                : {
                      key: column,
                      dir: column === 'name' ? 'asc' : 'desc',
                  },
        )
    }, [])

    return (
        <Dialog
            open={open}
            onClose={() => onClose()}
            maxWidth="md"
            fullWidth
            scroll="paper"
            PaperProps={{
                style: {
                    borderRadius: 12,
                    maxHeight: DIALOG_MAX_HEIGHT,
                    ...(loading && {
                        minHeight: 'min(82vh, calc(100vh - 48px))',
                    }),
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
            }}
        >
            <DialogTitle
                disableTypography
                style={{
                    padding: theme.spacing(2.5, 3),
                    background: `linear-gradient(135deg, ${fade(
                        BRAND_TEAL,
                        0.12,
                    )} 0%, ${fade(BRAND_TEAL, 0.04)} 100%)`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box display="flex" alignItems="flex-start" style={{ gap: 12 }}>
                    <Box
                        style={{
                            width: 44,
                            height: 44,
                            borderRadius: 10,
                            backgroundColor: fade(BRAND_TEAL, 0.18),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <DomainIcon
                            style={{ color: BRAND_TEAL, fontSize: 26 }}
                        />
                    </Box>
                    <Box flex={1} minWidth={0}>
                        <Typography
                            variant="h6"
                            style={{
                                fontWeight: 700,
                                letterSpacing: '-0.02em',
                                lineHeight: 1.25,
                            }}
                        >
                            Invoices by property
                        </Typography>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginTop: 4, lineHeight: 1.45 }}
                        >
                            All properties in the portfolio, including those
                            with zero invoices
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent
                style={{
                    padding: theme.spacing(2, 3, 0),
                    flex: '1 1 auto',
                    minHeight: loading
                        ? 'max(240px, min(48vh, calc(92vh - 420px)))'
                        : 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {loading ? (
                    <Box
                        display="flex"
                        flexDirection="column"
                        flex={1}
                        minHeight={0}
                        width="100%"
                    >
                        <Box
                            aria-hidden
                            style={{
                                height: 40,
                                marginBottom: theme.spacing(2),
                                borderRadius: 4,
                                backgroundColor: theme.palette.grey[100],
                                opacity: 0.35,
                            }}
                        />
                        <Box
                            aria-hidden
                            display="flex"
                            style={{
                                gap: theme.spacing(1),
                                marginBottom: theme.spacing(2),
                            }}
                        >
                            <Box
                                style={{
                                    height: 24,
                                    width: 88,
                                    borderRadius: 12,
                                    backgroundColor: theme.palette.grey[200],
                                    opacity: 0.5,
                                }}
                            />
                            <Box
                                style={{
                                    height: 24,
                                    width: 96,
                                    borderRadius: 12,
                                    backgroundColor: theme.palette.grey[200],
                                    opacity: 0.5,
                                }}
                            />
                            <Box
                                style={{
                                    height: 24,
                                    width: 112,
                                    borderRadius: 12,
                                    backgroundColor: theme.palette.grey[200],
                                    opacity: 0.5,
                                }}
                            />
                        </Box>
                        <Box
                            flex={1}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            style={{
                                height: BREAKDOWN_TABLE_VIEWPORT,
                                minHeight: BREAKDOWN_TABLE_VIEWPORT,
                                maxHeight: BREAKDOWN_TABLE_VIEWPORT,
                                borderRadius: 8,
                                border: `1px solid ${theme.palette.divider}`,
                                backgroundColor: theme.palette.grey[50],
                                gap: theme.spacing(2),
                            }}
                        >
                            <CircularProgress
                                size={36}
                                thickness={3}
                                style={{ color: BRAND_TEAL }}
                            />
                            <Typography variant="body1" color="textSecondary">
                                Loading breakdown…
                            </Typography>
                        </Box>
                    </Box>
                ) : rows.length === 0 ? (
                    <Box
                        py={5}
                        px={2}
                        textAlign="center"
                        borderRadius={8}
                        style={{
                            backgroundColor: theme.palette.grey[50],
                            border: `1px dashed ${theme.palette.divider}`,
                        }}
                    >
                        <Typography color="textSecondary" variant="body1">
                            No invoices found for any property in this
                            portfolio.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box
                            display="flex"
                            flexWrap="wrap"
                            alignItems="flex-start"
                            style={{
                                gap: theme.spacing(2),
                                marginBottom: theme.spacing(2),
                            }}
                        >
                            <TextField
                                size="small"
                                variant="outlined"
                                placeholder="Search by property name…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon
                                                style={{
                                                    color:
                                                        theme.palette.text
                                                            .secondary,
                                                    fontSize: 20,
                                                }}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                                style={{ flex: '1 1 200px', minWidth: 200 }}
                            />
                            <FormControl
                                variant="outlined"
                                size="small"
                                style={{
                                    minWidth: 180,
                                    flex: '0 0 auto',
                                    backgroundColor:
                                        theme.palette.background.paper,
                                }}
                            >
                                <InputLabel id="invoice-breakdown-property-tag-label">
                                    Property tag
                                </InputLabel>
                                <Select
                                    labelId="invoice-breakdown-property-tag-label"
                                    id="invoice-breakdown-property-tag-select"
                                    value={selectedTagId ?? 'all'}
                                    onChange={handleTagChange}
                                    label="Property tag"
                                >
                                    <MenuItem value="all">
                                        All properties
                                    </MenuItem>
                                    {userTags.map((tag) => (
                                        <MenuItem key={tag.id} value={tag.id}>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                            >
                                                <Box
                                                    style={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        backgroundColor:
                                                            tag.color,
                                                        marginRight: 8,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                {tag.name}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        {rows.length > 0 && workingRows.length === 0 ? (
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                py={5}
                                px={2}
                                textAlign="center"
                                borderRadius={8}
                                style={{
                                    flex: '1 1 auto',
                                    minHeight: BREAKDOWN_TABLE_VIEWPORT,
                                    maxHeight: BREAKDOWN_TABLE_VIEWPORT,
                                    backgroundColor: theme.palette.grey[50],
                                    border: `1px dashed ${theme.palette.divider}`,
                                }}
                            >
                                <Typography
                                    color="textSecondary"
                                    variant="body1"
                                >
                                    No properties in the selected property tag
                                    match this breakdown.
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Box
                                    display="flex"
                                    flexWrap="wrap"
                                    alignItems="center"
                                    style={{
                                        gap: theme.spacing(1),
                                        marginBottom: theme.spacing(2),
                                    }}
                                >
                                    <Chip
                                        size="small"
                                        label={`${workingRows.length} propert${
                                            workingRows.length === 1
                                                ? 'y'
                                                : 'ies'
                                        }`}
                                        style={{
                                            fontWeight: 600,
                                            backgroundColor: fade(
                                                BRAND_TEAL,
                                                0.14,
                                            ),
                                            border: `1px solid ${fade(
                                                BRAND_TEAL,
                                                0.35,
                                            )}`,
                                        }}
                                    />
                                    <Chip
                                        size="small"
                                        label={`${totals.invoiceCount.toLocaleString()} invoices`}
                                        style={{ fontWeight: 600 }}
                                    />
                                    <Chip
                                        size="small"
                                        label={currencyFormatter.format(
                                            totals.totalCost,
                                        )}
                                        style={{
                                            fontWeight: 700,
                                            color: BRAND_TEAL,
                                            backgroundColor: fade(
                                                BRAND_TEAL,
                                                0.08,
                                            ),
                                        }}
                                    />
                                </Box>
                                {search.trim() !== '' &&
                                    displayedRows.length > 0 && (
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            display="block"
                                            style={{
                                                marginTop: -theme.spacing(1),
                                                marginBottom: theme.spacing(
                                                    1.5,
                                                ),
                                            }}
                                        >
                                            Showing {displayedRows.length} of{' '}
                                            {workingRows.length} propert
                                            {workingRows.length === 1
                                                ? 'y'
                                                : 'ies'}
                                        </Typography>
                                    )}

                                {displayedRows.length === 0 ? (
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        py={4}
                                        px={2}
                                        textAlign="center"
                                        borderRadius={8}
                                        style={{
                                            flex: '1 1 auto',
                                            minHeight: BREAKDOWN_TABLE_VIEWPORT,
                                            maxHeight: BREAKDOWN_TABLE_VIEWPORT,
                                            backgroundColor:
                                                theme.palette.grey[50],
                                            border: `1px dashed ${theme.palette.divider}`,
                                        }}
                                    >
                                        <Typography
                                            color="textSecondary"
                                            variant="body1"
                                        >
                                            No properties match your search.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <TableContainer
                                        component={Paper}
                                        variant="outlined"
                                        style={{
                                            flex: '1 1 auto',
                                            height: BREAKDOWN_TABLE_VIEWPORT,
                                            minHeight: BREAKDOWN_TABLE_VIEWPORT,
                                            maxHeight: BREAKDOWN_TABLE_VIEWPORT,
                                            overflow: 'auto',
                                            borderRadius: 8,
                                            boxShadow: 'none',
                                            border: `1px solid ${theme.palette.divider}`,
                                        }}
                                    >
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell
                                                        style={{
                                                            backgroundColor:
                                                                theme.palette
                                                                    .grey[100],
                                                            borderBottom: `2px solid ${theme.palette.divider}`,
                                                            width: '52%',
                                                        }}
                                                    >
                                                        <TableSortLabel
                                                            active={
                                                                sort.key ===
                                                                'name'
                                                            }
                                                            direction={
                                                                sort.key ===
                                                                'name'
                                                                    ? sort.dir
                                                                    : 'asc'
                                                            }
                                                            onClick={() =>
                                                                handleSort(
                                                                    'name',
                                                                )
                                                            }
                                                            style={{
                                                                fontWeight: 700,
                                                                fontSize: 11,
                                                                textTransform:
                                                                    'uppercase',
                                                                letterSpacing:
                                                                    '0.06em',
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .text
                                                                        .secondary,
                                                            }}
                                                        >
                                                            Property
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        style={{
                                                            backgroundColor:
                                                                theme.palette
                                                                    .grey[100],
                                                            borderBottom: `2px solid ${theme.palette.divider}`,
                                                            width: '20%',
                                                        }}
                                                    >
                                                        <Box
                                                            display="flex"
                                                            justifyContent="flex-end"
                                                            width="100%"
                                                        >
                                                            <TableSortLabel
                                                                active={
                                                                    sort.key ===
                                                                    'invoice_count'
                                                                }
                                                                direction={
                                                                    sort.key ===
                                                                    'invoice_count'
                                                                        ? sort.dir
                                                                        : 'desc'
                                                                }
                                                                onClick={() =>
                                                                    handleSort(
                                                                        'invoice_count',
                                                                    )
                                                                }
                                                                style={{
                                                                    fontWeight: 700,
                                                                    fontSize: 11,
                                                                    textTransform:
                                                                        'uppercase',
                                                                    letterSpacing:
                                                                        '0.06em',
                                                                    color:
                                                                        theme
                                                                            .palette
                                                                            .text
                                                                            .secondary,
                                                                }}
                                                            >
                                                                Invoices
                                                            </TableSortLabel>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        style={{
                                                            backgroundColor:
                                                                theme.palette
                                                                    .grey[100],
                                                            borderBottom: `2px solid ${theme.palette.divider}`,
                                                            width: '28%',
                                                        }}
                                                    >
                                                        <Box
                                                            display="flex"
                                                            justifyContent="flex-end"
                                                            width="100%"
                                                        >
                                                            <TableSortLabel
                                                                active={
                                                                    sort.key ===
                                                                    'total_cost'
                                                                }
                                                                direction={
                                                                    sort.key ===
                                                                    'total_cost'
                                                                        ? sort.dir
                                                                        : 'desc'
                                                                }
                                                                onClick={() =>
                                                                    handleSort(
                                                                        'total_cost',
                                                                    )
                                                                }
                                                                style={{
                                                                    fontWeight: 700,
                                                                    fontSize: 11,
                                                                    textTransform:
                                                                        'uppercase',
                                                                    letterSpacing:
                                                                        '0.06em',
                                                                    color:
                                                                        theme
                                                                            .palette
                                                                            .text
                                                                            .secondary,
                                                                }}
                                                            >
                                                                Total cost
                                                            </TableSortLabel>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {displayedRows.map(
                                                    (row, index) => {
                                                        const rowTags = tagsForApartment(
                                                            row.apartment_id,
                                                            userTags,
                                                        )
                                                        return (
                                                            <TableRow
                                                                key={
                                                                    row.apartment_id
                                                                }
                                                                hover
                                                                style={{
                                                                    backgroundColor:
                                                                        index %
                                                                            2 ===
                                                                        0
                                                                            ? undefined
                                                                            : fade(
                                                                                  theme
                                                                                      .palette
                                                                                      .common
                                                                                      .black,
                                                                                  0.02,
                                                                              ),
                                                                }}
                                                            >
                                                                <TableCell
                                                                    component="th"
                                                                    scope="row"
                                                                    style={{
                                                                        fontWeight: 500,
                                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                                        verticalAlign:
                                                                            'middle',
                                                                    }}
                                                                >
                                                                    <Box
                                                                        display="flex"
                                                                        flexWrap="wrap"
                                                                        alignItems="center"
                                                                        style={{
                                                                            gap: 8,
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            component="span"
                                                                            variant="body2"
                                                                            style={{
                                                                                fontWeight: 500,
                                                                                lineHeight: 1.4,
                                                                            }}
                                                                        >
                                                                            {row.name ||
                                                                                '—'}
                                                                        </Typography>
                                                                        {rowTags.map(
                                                                            (
                                                                                tag,
                                                                            ) => (
                                                                                <Chip
                                                                                    key={
                                                                                        tag.id
                                                                                    }
                                                                                    size="small"
                                                                                    label={
                                                                                        tag.name
                                                                                    }
                                                                                    style={{
                                                                                        height: 22,
                                                                                        fontSize: 11,
                                                                                        fontWeight: 600,
                                                                                        backgroundColor: fade(
                                                                                            tag.color,
                                                                                            0.18,
                                                                                        ),
                                                                                        border: `1px solid ${fade(
                                                                                            tag.color,
                                                                                            0.45,
                                                                                        )}`,
                                                                                    }}
                                                                                />
                                                                            ),
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell
                                                                    align="right"
                                                                    style={{
                                                                        fontFamily:
                                                                            'Roboto Mono, monospace',
                                                                        fontSize: 13,
                                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                                    }}
                                                                >
                                                                    {row.invoice_count.toLocaleString()}
                                                                </TableCell>
                                                                <TableCell
                                                                    align="right"
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        color: BRAND_TEAL,
                                                                        fontVariantNumeric:
                                                                            'tabular-nums',
                                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                                    }}
                                                                >
                                                                    {currencyFormatter.format(
                                                                        row.total_cost,
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        )
                                                    },
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </>
                        )}
                    </>
                )}
            </DialogContent>

            {!loading && workingRows.length > 0 && (
                <Box
                    px={3}
                    py={2}
                    style={{
                        borderTop: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.grey[50],
                    }}
                >
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        alignItems="center"
                        justifyContent="space-between"
                        style={{ gap: theme.spacing(2) }}
                    >
                        <Typography
                            variant="subtitle2"
                            color="textSecondary"
                            style={{ fontWeight: 600 }}
                        >
                            Portfolio total
                        </Typography>
                        <Box
                            display="flex"
                            flexWrap="wrap"
                            alignItems="baseline"
                            style={{ gap: theme.spacing(3) }}
                        >
                            <Typography variant="body2">
                                <strong>
                                    {totals.invoiceCount.toLocaleString()}
                                </strong>{' '}
                                invoices
                            </Typography>
                            <Typography
                                variant="h6"
                                style={{
                                    fontWeight: 700,
                                    color: BRAND_TEAL,
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {currencyFormatter.format(totals.totalCost)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )}

            <DialogActions
                style={{
                    padding: theme.spacing(1.5, 3),
                    borderTop: `1px solid ${theme.palette.divider}`,
                    backgroundColor: '#fff',
                }}
            >
                <Button
                    onClick={() => onClose()}
                    variant="contained"
                    color="primary"
                    disableElevation
                    style={{
                        textTransform: 'none',
                        fontWeight: 600,
                        paddingLeft: theme.spacing(3),
                        paddingRight: theme.spacing(3),
                        borderRadius: 8,
                        backgroundColor: BRAND_TEAL,
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}
